import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import {
  advanceQuestionFlowAction,
  analyzeSuggestionsAction,
  DefectAssistantImage,
  DefectAssistantSuggestion,
  fetchDefectCodeAdviceAction,
  isQuestionUnanswered,
  OptionValue,
  QnA,
  QuestionTypes,
  ReportDefectBody,
} from '../actions/defectAssistant'
import type { RootState } from '../index'
import { getDefectGroupsLabel } from '../lib/createDefectFormUtils'

/** Mutually exclusive position in the assistant workflow. */
export type FlowStep =
  | { step: 'idle' }
  | { step: 'analyzing' }
  | { step: 'choosing' }
  | { step: 'detailing' }
  | { step: 'questioning'; loading: boolean }
  | { step: 'failed'; source: 'analyze' | 'questions' }

export type DefectCodeAdviceStatus = 'loading' | 'ready' | 'failed'

export interface DefectCodeAdviceEntry {
  status: DefectCodeAdviceStatus
  text: string
}

/**
 * A suggestion generation retained as part of the scrollable history.
 * `detail` is `null` until submitted; an empty string records submission without extra detail.
 */
export interface SuggestionRound {
  generationId: string | null
  suggestions: DefectAssistantSuggestion[]
  metaOptions: OptionValue[]
  detail: string | null
  excluded: boolean
}

export interface CreateDefectAssistantState {
  flow: FlowStep
  suggestionAttempts: number
  images: DefectAssistantImage[]
  baseComment: string
  discardedDefectCodes: string[]
  rounds: SuggestionRound[]
  /** Round containing the selected defect code, or `null` when none is selected. */
  pickedRoundIdx: number | null
  /** Advice keyed by generation and code prefix so late responses update their source round. */
  defectCodeAdviceByGeneration: Record<string, Record<string, DefectCodeAdviceEntry>>
  body: ReportDefectBody
  questionAnswers: QnA[]
}

type InitializeCreateDefectAssistantPayload = {
  comment: string
  images: DefectAssistantImage[]
  vehicleType: string
  installationGroup: string
}

const initialState: CreateDefectAssistantState = {
  flow: { step: 'idle' },
  suggestionAttempts: 1,
  images: [],
  baseComment: '',
  discardedDefectCodes: [],
  rounds: [],
  pickedRoundIdx: null,
  defectCodeAdviceByGeneration: {},
  body: {
    userComment: '',
    aiComment: '',
    defectCode: '',
    defectType: '',
    restrictionCode: '',
    installationGroup: '',
    vehicleSeries: '',
  },
  questionAnswers: [],
}

const setDefectCodeAdvice = (
  state: CreateDefectAssistantState,
  generationId: string,
  prefix: string,
  entry: DefectCodeAdviceEntry
): void => {
  if (!state.defectCodeAdviceByGeneration[generationId]) {
    state.defectCodeAdviceByGeneration[generationId] = {}
  }
  state.defectCodeAdviceByGeneration[generationId][prefix] = entry
}

export const createDefectAssistantSlice = createSlice({
  name: 'createDefectAssistant',
  initialState,
  reducers: {
    initializeCreateDefectAssistant: (
      _state,
      { payload }: PayloadAction<InitializeCreateDefectAssistantPayload>
    ) => ({
      ...initialState,
      images: payload.images,
      baseComment: payload.comment.trim(),
      body: {
        userComment: payload.comment.trim(),
        aiComment: '',
        defectCode: '',
        defectType: '',
        restrictionCode: '',
        installationGroup: payload.installationGroup,
        vehicleSeries: payload.vehicleType,
      },
    }),

    clearCreateDefectAssistantErrors: (state) => {
      if (state.flow.step === 'failed') {
        state.flow = { step: 'choosing' }
      }
    },

    selectSuggestion: (
      state,
      { payload }: PayloadAction<{ suggestion: DefectAssistantSuggestion; roundIdx: number }>
    ) => {
      state.rounds = state.rounds.slice(0, payload.roundIdx + 1)
      state.pickedRoundIdx = payload.roundIdx
      state.body.defectCode = payload.suggestion.defectCode
      state.body.defectType = ''
      state.body.itemId = undefined
      state.body.aiComment = ''
      state.questionAnswers = []
      state.flow = { step: 'questioning', loading: false }
    },

    submitAdditionalDetails: (state, { payload }: PayloadAction<string>) => {
      const trimmedDetails = payload.trim()
      const lastRound = state.rounds[state.rounds.length - 1]
      if (lastRound) {
        lastRound.detail = trimmedDetails
      }
      state.flow = { step: 'choosing' }

      if (trimmedDetails.length === 0) {
        return
      }
      state.body.userComment =
        state.body.userComment.length > 0
          ? `${state.body.userComment}\n\n${trimmedDetails}`
          : trimmedDetails
    },

    applyQuestionAnswer: (state, { payload }: PayloadAction<OptionValue[]>) => {
      const targetIdx = state.questionAnswers.findIndex(isQuestionUnanswered)
      if (targetIdx === -1) {
        return
      }

      const targetQnA = state.questionAnswers[targetIdx]
      state.questionAnswers = state.questionAnswers.map((qna, idx) =>
        idx === targetIdx ? { ...qna, answers: payload } : qna
      )

      const first = payload[0]
      if (first?.value != null) {
        if (targetQnA.questionType === QuestionTypes.DEFECT_TYPE) {
          state.body.defectType = String(first.value)
        }
        if (targetQnA.questionType === QuestionTypes.PHYSICAL_ITEM) {
          state.body.itemId = String(first.value)
        }
      }
    },

    excludeSuggestions: (state, { payload: roundIdx }: PayloadAction<number>) => {
      const round = state.rounds[roundIdx]
      if (!round) {
        return
      }
      const existing = new Set(state.discardedDefectCodes)
      round.suggestions.forEach((s) => {
        existing.add(s.defectCode)
      })
      state.discardedDefectCodes = Array.from(existing)
      round.excluded = true
      state.suggestionAttempts = state.suggestionAttempts + 1
      if (roundIdx === state.rounds.length - 1 && round.detail !== null) {
        round.detail = null
        state.body.userComment = [
          state.baseComment,
          ...state.rounds.slice(0, roundIdx).map((r) => r.detail ?? ''),
        ]
          .filter((part) => part.length > 0)
          .join('\n\n')
      }
      state.flow = { step: 'detailing' }
      state.pickedRoundIdx = null
      state.body.defectCode = ''
      state.body.defectType = ''
      state.body.itemId = undefined
      state.body.aiComment = ''
      state.questionAnswers = []
    },

    rewindToRound: (state, { payload: roundIdx }: PayloadAction<number>) => {
      const round = state.rounds[roundIdx]
      if (!round) {
        return
      }
      const existing = new Set(state.discardedDefectCodes)
      round.suggestions.forEach((s) => {
        existing.add(s.defectCode)
      })
      state.discardedDefectCodes = Array.from(existing)
      state.suggestionAttempts = state.suggestionAttempts + 1
      state.rounds = state.rounds.slice(0, roundIdx + 1)
      round.excluded = true
      round.detail = null
      state.body.userComment = [
        state.baseComment,
        ...state.rounds.slice(0, roundIdx).map((r) => r.detail ?? ''),
      ]
        .filter((part) => part.length > 0)
        .join('\n\n')
      state.flow = { step: 'detailing' }
      state.pickedRoundIdx = null
      state.body.defectCode = ''
      state.body.defectType = ''
      state.body.itemId = undefined
      state.body.aiComment = ''
      state.questionAnswers = []
    },

    resetAnswersFromIndex: (state, { payload }: PayloadAction<number>) => {
      state.questionAnswers.slice(payload).forEach((qna) => {
        if (qna.questionType === QuestionTypes.DEFECT_TYPE) state.body.defectType = ''
        if (qna.questionType === QuestionTypes.PHYSICAL_ITEM) state.body.itemId = undefined
      })

      state.questionAnswers = state.questionAnswers
        .slice(0, payload + 1)
        .map((qna, idx) => (idx >= payload ? { ...qna, answers: null } : qna))

      state.body.aiComment = ''
    },

    resetCreateDefectAssistant: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(analyzeSuggestionsAction.pending, (state) => {
      state.flow = { step: 'analyzing' }
    })

    builder.addCase(analyzeSuggestionsAction.fulfilled, (state, { payload }) => {
      if (state.flow.step === 'questioning') return
      state.rounds.push({
        generationId: payload.generationId,
        suggestions: payload.suggestions,
        metaOptions: payload.metaOptions,
        detail: null,
        excluded: false,
      })
      state.flow = { step: 'choosing' }
    })

    builder.addCase(fetchDefectCodeAdviceAction.pending, (state, action) => {
      setDefectCodeAdvice(state, action.meta.arg.generationId, action.meta.arg.prefix, {
        status: 'loading',
        text: '',
      })
    })

    builder.addCase(fetchDefectCodeAdviceAction.fulfilled, (state, action) => {
      const { prefix, defectCodeAdvice, ready } = action.payload
      setDefectCodeAdvice(
        state,
        action.meta.arg.generationId,
        prefix,
        ready && defectCodeAdvice
          ? { status: 'ready', text: defectCodeAdvice }
          : { status: 'failed', text: '' }
      )
    })

    builder.addCase(fetchDefectCodeAdviceAction.rejected, (state, action) => {
      setDefectCodeAdvice(state, action.meta.arg.generationId, action.meta.arg.prefix, {
        status: 'failed',
        text: '',
      })
    })

    builder.addCase(analyzeSuggestionsAction.rejected, (state) => {
      state.flow = { step: 'failed', source: 'analyze' }
    })

    builder.addCase(advanceQuestionFlowAction.pending, (state) => {
      state.flow = { step: 'questioning', loading: true }
    })

    builder.addCase(advanceQuestionFlowAction.fulfilled, (state, { payload }) => {
      state.flow = { step: 'questioning', loading: false }
      state.body = {
        ...payload.body,
        installationGroup: payload.body.installationGroup ?? state.body.installationGroup,
        vehicleSeries: payload.body.vehicleSeries ?? state.body.vehicleSeries,
        itemId:
          payload.body.itemId === undefined ? state.body.itemId : payload.body.itemId ?? undefined,
      }
      state.questionAnswers = payload.questionAnswers
    })

    builder.addCase(advanceQuestionFlowAction.rejected, (state, action) => {
      if (action.meta.aborted) return
      state.flow = { step: 'failed', source: 'questions' }
    })
  },
})

export const {
  initializeCreateDefectAssistant,
  clearCreateDefectAssistantErrors,
  selectSuggestion,
  submitAdditionalDetails,
  applyQuestionAnswer,
  excludeSuggestions,
  rewindToRound,
  resetAnswersFromIndex,
  resetCreateDefectAssistant,
} = createDefectAssistantSlice.actions

export const selectCreateDefectAssistantState = (state: RootState): CreateDefectAssistantState =>
  state.createDefectAssistant

export const selectAnsweredQuestions = (state: RootState): QnA[] => {
  const answers = state.createDefectAssistant.questionAnswers
  const firstUnansweredIdx = answers.findIndex(isQuestionUnanswered)
  return firstUnansweredIdx >= 0 ? answers.slice(0, firstUnansweredIdx) : answers
}

export const selectCurrentQuestion = (state: RootState): QnA | undefined =>
  state.createDefectAssistant.questionAnswers.find(isQuestionUnanswered)

export const selectAssistantIsComplete = (state: RootState): boolean => {
  const qnas = state.createDefectAssistant.questionAnswers
  return qnas.length > 0 && qnas.every((qna) => !isQuestionUnanswered(qna))
}

export const selectProductGroupLabel =
  (state: RootState) =>
  (productGroupPrefix: string): string => {
    const treeRow = state.defect.defectTrees.data.find((row) =>
      row.defectCode.startsWith(productGroupPrefix)
    )
    if (!treeRow) return ''
    return getDefectGroupsLabel(treeRow)
  }

export default createDefectAssistantSlice.reducer
